import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { validateResetToken, submitPasswordReset } from '../components/authentication/functions/api_calls';
import { readAxiosDetail } from '../components/company/api_calls';

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [invalid, setInvalid] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await validateResetToken(token);
        if (!cancelled) {
          if (info.is_expired) {
            setInvalid(true);
          } else {
            setEmail(info.email);
          }
        }
      } catch {
        if (!cancelled) setInvalid(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!password) {
      setSubmitError(t('resetPassword.passwordRequired'));
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError(t('resetPassword.passwordsDoNotMatch'));
      return;
    }

    setSubmitting(true);
    try {
      await submitPasswordReset(token!, password);
      setSuccess(true);
      setTimeout(() => navigate('/', { replace: true }), 2000);
    } catch (e) {
      const msg = readAxiosDetail(e);
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid')) {
        setInvalid(true);
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CenteredContainer>
        <CircularProgress />
        <Typography color="text.secondary" sx={{ mt: 2 }}>{t('resetPassword.validating')}</Typography>
      </CenteredContainer>
    );
  }

  if (invalid) {
    return (
      <CenteredContainer>
        <Paper sx={{ p: 4, maxWidth: 440, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>{t('resetPassword.invalidTitle')}</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('resetPassword.invalidMessage')}
          </Typography>
          <Button variant="outlined" component={RouterLink} to="/forgot-password">
            {t('resetPassword.requestNewLink')}
          </Button>
        </Paper>
      </CenteredContainer>
    );
  }

  if (success) {
    return (
      <CenteredContainer>
        <Paper sx={{ p: 4, maxWidth: 440, width: '100%', textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('resetPassword.successRedirect')}
          </Alert>
          <Button variant="outlined" component={RouterLink} to="/">
            {t('resetPassword.goToLogin')}
          </Button>
        </Paper>
      </CenteredContainer>
    );
  }

  return (
    <CenteredContainer>
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t('resetPassword.heading')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('resetPassword.subheading')}
        </Typography>

        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <TextField
          fullWidth
          label={t('resetPassword.emailLabel')}
          value={email}
          disabled
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('resetPassword.newPasswordLabel')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
          required
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('resetPassword.confirmPasswordLabel')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          size="small"
          required
          sx={{ mb: 3 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSubmit();
          }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={20} /> : t('resetPassword.submitButton')}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          <RouterLink to="/" style={{ color: 'inherit' }}>{t('resetPassword.backToLogin')}</RouterLink>
        </Typography>
      </Paper>
    </CenteredContainer>
  );
}

function CenteredContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}
